// ============================================================================
// GLISSER-DÉPOSER DE TUILES — la mécanique, une fois pour toutes (charte §4).
//
// UTILITÉ : réordonner des éléments à la souris ou au doigt, dans une liste comme dans une grille.
// Un seul patron pour les articles rapides, les photos de fiche article, les lignes du popover
// « Colonnes » — et tout réordonnancement à venir. Trois recettes séparées, c'était garantir que la
// troisième n'aurait ni l'annulation au clavier ni le seuil de saisie des deux premières.
//
// ⚠️ LE MODULE NE DÉCIDE DE RIEN : il rend l'ordre VOULU, l'appelant en fait ce qu'il veut (état
// local, écriture serveur, rien du tout). Il ne connaît ni les colonnes, ni les photos, ni les
// articles — c'est ce qui lui permet de servir les trois.
//
// ⚠️ IL NE REMPLACE PAS LE CLAVIER, IL LE DOUBLE. Le popover « Colonnes » déplace déjà aux flèches
// ↑/↓ ; les deux gestes doivent produire EXACTEMENT le même résultat, donc passer par la même
// fonction de déplacement côté appelant. Un glisser qui aurait sa propre arithmétique finirait par
// diverger du clavier, et c'est le clavier qui perdrait — personne ne le teste à la main.
// ============================================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TILE_DRAG, TILE_DRAG_REST } from '@orgazone/ui-tokens';

/** Un rectangle mesuré — le sous-ensemble de `DOMRect` dont le calcul a besoin. */
export interface TileRect { top: number; left: number; width: number; height: number }
/** Une tuile mesurée, dans l'ordre où elle est RENDUE. */
export interface MeasuredTile { key: string; rect: TileRect }

/**
 * OÙ LA TUILE SAISIE DOIT ATTERRIR, d'après la position du pointeur.
 *
 * ⚠️ PURE ET EXPORTÉE POUR ÊTRE PROUVÉE SANS NAVIGATEUR : c'est la seule partie du glisser qui décide
 * quelque chose, et une erreur d'un cran ici se voit à peine à l'œil tout en réordonnant faux.
 *
 * ⚠️ ON COMPARE AU CENTRE DE CHAQUE TUILE, pas à son bord : au bord, la tuile bascule dès qu'on
 * l'effleure et l'ordre clignote pendant tout le geste. Au centre, il faut avoir vraiment dépassé la
 * voisine — ce qui correspond à l'intention.
 *
 * ⚠️ EN GRILLE (`xy`), C'EST LA DISTANCE AU CENTRE QUI DÉCIDE, pas la seule ordonnée : deux tuiles
 * d'une même rangée ont la même ordonnée, et un classement vertical les rendrait interchangeables.
 */
export function dropIndex(tiles: readonly MeasuredTile[], pointer: { x: number; y: number }, axis: 'y' | 'xy'): number {
  if (tiles.length === 0) return 0;
  if (axis === 'y') {
    for (let i = 0; i < tiles.length; i += 1) {
      const r = (tiles[i] as MeasuredTile).rect;
      if (pointer.y < r.top + r.height / 2) return i;
    }
    return tiles.length;
  }
  let meilleur = 0;
  let distance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < tiles.length; i += 1) {
    const r = (tiles[i] as MeasuredTile).rect;
    const dx = pointer.x - (r.left + r.width / 2);
    const dy = pointer.y - (r.top + r.height / 2);
    const d = dx * dx + dy * dy;
    if (d < distance) { distance = d; meilleur = i; }
  }
  // Dépassé le centre de la tuile la plus proche ⇒ on se place APRÈS elle.
  const r = (tiles[meilleur] as MeasuredTile).rect;
  return pointer.x > r.left + r.width / 2 ? meilleur + 1 : meilleur;
}

/**
 * L'ordre obtenu en déplaçant une clé à un index donné.
 * ⚠️ L'INDEX EST CELUI DE LA LISTE **SANS** LA CLÉ SAISIE : c'est ce que le pointeur désigne
 * réellement, la tuile d'origine ayant cédé sa place au placeholder.
 */
export function reorderTo(order: readonly string[], key: string, index: number): string[] {
  // ⚠️ UNE CLÉ INCONNUE NE S'INVENTE PAS UNE PLACE : l'ordre appartient à l'appelant, et l'y insérer
  //    créerait une entrée fantôme — pour les colonnes, une clé qui ne correspond à aucune colonne,
  //    persistée telle quelle dans les réglages du compte.
  if (!order.includes(key)) return [...order];
  const reste = order.filter((k) => k !== key);
  const i = Math.max(0, Math.min(index, reste.length));
  reste.splice(i, 0, key);
  return reste;
}

/**
 * Le déplacement est-il permis ? Une tuile FIGÉE ne se saisit pas, et rien ne s'insère à sa place.
 * ⚠️ SANS CETTE GARDE, une colonne épinglée se laisserait glisser puis reviendrait à sa place au
 * rendu suivant — le geste réussirait à l'écran et échouerait dans les faits.
 */
export function estDeplacable(key: string, movable?: (key: string) => boolean): boolean {
  return movable ? movable(key) : true;
}

/**
 * DEUX ORDRES SONT-ILS LE MÊME ?
 *
 * ⚠️ ÉLÉMENT PAR ÉLÉMENT, et non par une chaîne jointe sur un séparateur « impossible ». La version
 * précédente joignait sur un caractère NUL — invisible dans l'éditeur, invisible dans une revue, et
 * effectivement écrit en dur dans la source par un script d'édition. Une comparaison qu'on peut lire
 * vaut mieux qu'une astuce qu'on doit deviner.
 *
 * ⚠️ C'EST ELLE QUI DÉCIDE S'IL Y A ÉCRITURE : un glisser reposé au même endroit ne doit déclencher
 * ni appel serveur ni « réglages enregistrés ».
 */
export function memeOrdre(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * LA PLACE VISÉE dans l'ordre, une fois la tuile saisie RETIRÉE.
 *
 * ⚠️ C'EST ICI QUE SE LOGE L'ERREUR D'UN CRAN, et elle est presque invisible à l'œil : `reorderTo`
 * insère dans la liste PRIVÉE de la clé saisie, alors que l'index lu dans l'ordre COMPLET vaut un de
 * plus dès que la tuile vient d'au-dessus de sa cible. Une tuile descendue d'un rang atterrissait
 * donc deux rangs plus bas — et remontée, au bon endroit. Asymétrie parfaite, cause introuvable si
 * l'on ne compare pas les deux sens.
 *
 * `cible` absente ⇒ la fin de la liste (on a dépassé la dernière tuile).
 */
export function indexAfterRemoval(order: readonly string[], key: string, target: string | undefined): number {
  const reste = order.filter((k) => k !== key);
  if (!target) return reste.length;
  const i = reste.indexOf(target);
  return i < 0 ? reste.length : i;
}

/** La tuile saisie, en geste « en place » : estompée à sa case, le temps du geste et de l'atterrissage. */
const FADED: CSSProperties = { opacity: TILE_DRAG.inPlace.faded, transition: `opacity ${TILE_DRAG.inPlace.fadeMs}ms` };

export interface TileDragOptions {
  /** L'ordre COURANT des clés — la vérité appartient à l'appelant, jamais au geste. */
  keys: readonly string[];
  /** L'ordre VOULU, au dépôt. Appelé UNE fois, et seulement si l'ordre a changé. */
  onReorder: (next: string[]) => void;
  /** Une clé est-elle déplaçable ? Défaut : toutes le sont. */
  movable?: (key: string) => boolean;
  /** Liste verticale (`y`, défaut) ou grille de tuiles (`xy`). */
  axis?: 'y' | 'xy';
  /** Coupe le geste — lecture seule, chargement, écran verrouillé. */
  disabled?: boolean;
  /**
   * SÉLECTEUR CSS DES ZONES QUI NE SAISISSENT PAS — boutons posés SUR la tuile.
   *
   * ⚠️ IL EXISTE PARCE QUE « toute la tuile est la prise » ET « ce bouton doit rester cliquable » sont
   * tous deux vrais. Sur une grille d’images, l’image EST l’objet qu’on déplace — une poignée dédiée
   * obligerait à viser un coin de 20 px. Mais l’étoile et la croix posées dessus doivent, elles,
   * répondre au clic.
   * ⚠️ ARRÊTER LE `pointerdown` DEPUIS L’APPELANT NE SUFFIT PAS : le seuil de 5 px rattrape un clic
   * net, pas une main qui bouge un peu — et l’action passe alors une fois sur deux. C’est au geste de
   * REFUSER DE PARTIR, pas au bouton de se défendre.
   * ⚠️ Un SÉLECTEUR et non un rappel : c’est ce que fait la maquette (`closest('[data-ph-x],[data-ph-star]')`),
   * et ça permet de marquer les zones en JSX sans porter d’état.
   */
  ignoreSelector?: string;
  /**
   * LA CLASSE QUI PORTE LE STYLE DES TUILES — celle de leur conteneur d'origine.
   *
   * ⚠️ SANS ELLE, LE FANTÔME EST NU. Il est porté dans `document.body` pour échapper au découpage de
   * son conteneur ; il sort donc de toute règle écrite en descendance (`.dt-col-panel .dt-col-row`,
   * et c'est ainsi qu'est écrite la charte). Résultat mesuré : une tuile qui se déplace **vide** —
   * ni texte ni icône — pendant que sa place d'origine, elle, restait parfaitement rendue.
   * ⚠️ Le module NEUTRALISE la boîte de cette classe (fond, bordure, rembourrage) par des styles en
   * ligne : on emprunte son rôle de PORTÉE, pas son apparence de conteneur.
   */
  ghostScope?: string;
  /**
   * LA TUILE SAISIE RESTE À SA PLACE, ESTOMPÉE (`TILE_DRAG.inPlace`), au lieu de céder la place au creux : l'appelant
   * la rend comme toujours (`placeholder` reste `null`), et c'est elle que les voisines contournent. Au lâcher, le
   * fantôme glisse jusqu'à sa case puis s'efface — sauf quand le mouvement réduit est demandé.
   * ⚠️ L'ORDRE EST RENDU AU LÂCHER, pas à l'atterrissage : l'animation ne retarde aucune écriture.
   */
  inPlace?: boolean;
}

export interface TileDragApi {
  /** L'ordre À RENDRE : l'aperçu pendant le geste, l'ordre d'origine sinon. */
  order: readonly string[];
  /** La clé saisie — la tuile à remplacer par `placeholder`. `null` hors geste. */
  grabbed: string | null;
  /** À étaler sur CHAQUE tuile : mesure, styles de repos, identité. */
  tile: (key: string) => { ref: (el: HTMLElement | null) => void; style: CSSProperties; 'data-tile-key': string };
  /** À étaler sur la PRISE — poignée dédiée, ou la tuile elle-même si tout est saisissable. */
  handle: (key: string) => { onPointerDown: (e: ReactPointerEvent) => void; style: CSSProperties };
  /**
   * LE CREUX À RENDRE **À LA PLACE** DE LA TUILE SAISIE — déjà dimensionné, déjà clé. `null` hors geste.
   *
   * ⚠️ FABRIQUÉ ICI, ET PAS EN RESTYLANT LA TUILE DE L'APPELANT. Un style en ligne n'atteint pas les
   * descendants : la ligne repeinte gardait ses cases à cocher et ses pastilles, qui portent leur
   * propre fond — le creux affichait donc la moitié de son contenu (constaté à l'écran le
   * 2026-08-19). Le repeindre en cascade demanderait une recette CSS, et le faire masquer par chaque
   * consommateur mettrait la même règle en trois exemplaires.
   * ⚠️ IL PORTE LE `ref` DE LA TUILE qu'il remplace : c'est lui qui est mesuré pendant le geste, donc
   * les voisines continuent de se placer par rapport à sa vraie surface.
   */
  placeholder: ReactNode;
  /** Le fantôme, à rendre tel quel dans l'arbre (il se porte tout seul). `null` hors geste. */
  ghost: ReactNode;
}

/**
 * LE GESTE DE GLISSER, branché sur n'importe quelle liste d'éléments identifiés par une clé.
 *
 * ⚠️ LE FANTÔME EST UN CLONE DU DOM RÉEL, pas un rendu parallèle : l'appelant n'a pas à décrire une
 * seconde fois sa tuile, et le fantôme ne peut donc pas diverger de l'original. Il est `fixed`,
 * `pointer-events:none`, et il ne reçoit rien.
 *
 * ⚠️ `Échap` ANNULE ET TOUT REPREND SA PLACE — sans commit. Un geste qu'on ne peut pas annuler oblige
 * à réussir du premier coup, sur un écran où l'on manipule le réglage d'un autre.
 *
 * ⚠️ LES VOISINES GLISSENT PENDANT LE SURVOL (FLIP), jamais au dépôt : on doit voir où la tuile va
 * tomber AVANT de lâcher. C'est la différence entre réordonner et parier.
 */
export function useTileDrag({ keys, onReorder, movable, axis = 'y', disabled, ghostScope, ignoreSelector, inPlace = false }: TileDragOptions): TileDragApi {
  // ⚠️ CET ÉTAT EXISTE POUR QUE L'EFFET SE REJOUE, et c'est tout son rôle. Le geste ne partait
  //    JAMAIS : `pointerdown` n'écrivait qu'une ref, puis appelait `setGrabbed(null)` sur une valeur
  //    déjà nulle — React ne re-rendait pas, l'effet ne se rejouait pas, et aucun écouteur de
  //    `pointermove` n'était posé. La poignée existait, le curseur changeait, rien ne bougeait.
  //    Une ref ne réveille rien : ce qui doit déclencher un effet doit être un ÉTAT.
  const [pointe, setPointe] = useState<string | null>(null);
  const [grabbed, setGrabbed] = useState<string | null>(null);
  const [apercu, setApercu] = useState<readonly string[] | null>(null);
  const [ghostBox, setGhostBox] = useState<{ x: number; y: number; w: number; h: number; html: string; landing?: true } | null>(null);
  // L'atterrissage du fantôme en cours (geste « en place ») : on l'arrête si un geste repart ou si l'écran se démonte.
  const landing = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (landing.current) clearTimeout(landing.current); }, []);

  const elements = useRef(new Map<string, HTMLElement>());
  const depart = useRef<{ key: string; x: number; y: number; saisi: boolean } | null>(null);
  // Décalage entre le coin de la tuile et le point saisi : sans lui, le fantôme saute sous le curseur.
  const prise = useRef({ dx: 0, dy: 0 });
  const rectsAvant = useRef(new Map<string, TileRect>());

  const ordre = apercu ?? keys;

  const mesurer = useCallback((): MeasuredTile[] => {
    const out: MeasuredTile[] = [];
    for (const k of ordre) {
      const el = elements.current.get(k);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      out.push({ key: k, rect: { top: r.top, left: r.left, width: r.width, height: r.height } });
    }
    return out;
  }, [ordre]);

  /** Photographie les positions AVANT un changement d'ordre — la moitié « First » du FLIP. */
  const photographier = useCallback(() => {
    rectsAvant.current.clear();
    for (const [k, el] of elements.current) {
      const r = el.getBoundingClientRect();
      rectsAvant.current.set(k, { top: r.top, left: r.left, width: r.width, height: r.height });
    }
  }, []);

  // ⚠️ FLIP : après le rendu du nouvel ordre, on remet visuellement chaque tuile où elle ÉTAIT, puis
  //    on la laisse rejoindre sa place. Sans ce détour, les tuiles se téléportent et le regard perd
  //    laquelle a bougé.
  useLayoutEffect(() => {
    if (rectsAvant.current.size === 0) return;
    for (const [k, el] of elements.current) {
      const avant = rectsAvant.current.get(k);
      if (!avant) continue;
      const after = el.getBoundingClientRect();
      const dx = avant.left - after.left;
      const dy = avant.top - after.top;
      if (dx === 0 && dy === 0) continue;
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      // Deux trames : la première applique l'inversion, la seconde lance le retour.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transition = `transform ${TILE_DRAG.flipMs}ms ease`;
          el.style.transform = '';
        });
      });
    }
    rectsAvant.current.clear();
  }, [ordre]);

  const terminer = useCallback((commit: boolean) => {
    const d = depart.current;
    depart.current = null;
    prise.current = { dx: 0, dy: 0 };
    setPointe(null);
    // ⚠️ EN PLACE, LE FANTÔME REJOINT LA CASE où la tuile se tient déjà (l'ordre a changé pendant le survol), puis
    //    s'efface ; la tuile reste estompée jusque-là. Sans mouvement réduit seulement : sinon tout se pose d'un coup.
    const el = d?.saisi ? elements.current.get(d.key) : undefined;
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    if (inPlace && commit && el && !reduced) {
      const r = el.getBoundingClientRect();
      setGhostBox((g) => (g ? { ...g, x: r.left, y: r.top, w: r.width, h: r.height, landing: true } : g));
      if (landing.current) clearTimeout(landing.current);
      landing.current = setTimeout(() => { landing.current = null; setGhostBox(null); setGrabbed(null); }, TILE_DRAG.inPlace.landingMs + 70);
    } else {
      setGhostBox(null);
      setGrabbed(null);
    }
    const vise = apercu;
    setApercu(null);
    if (!commit || !d?.saisi || !vise) return;
    // ⚠️ RENDU UNE SEULE FOIS, et seulement si l'ordre a VRAIMENT changé : un glisser reposé au même
    //    endroit ne doit pas déclencher d'écriture serveur ni de « réglages enregistrés ».
    if (!memeOrdre(vise, keys)) onReorder([...vise]);
  }, [apercu, keys, onReorder, inPlace]);

  // ⚠️ CE MIROIR EXISTE POUR QUE LES ÉCOUTEURS SOIENT POSÉS UNE FOIS PAR GESTE. Écrits en
  //    dépendances, `ordre`/`keys`/`terminer` changent d'identité à chaque rendu — et comme chaque
  //    mouvement de souris provoque un rendu (le fantôme suit le curseur), les quatre écouteurs
  //    étaient détachés puis rattachés à CHAQUE pixel parcouru. Le geste n'en cassait pas, mais on
  //    reconstruisait tout par trame pour rien.
  const vif = useRef({ ordre, keys, axis, movable, mesurer, photographier, terminer });
  vif.current = { ordre, keys, axis, movable, mesurer, photographier, terminer };

  // ⚠️ LES ÉCOUTEURS VIVENT SUR LA FENÊTRE, pas sur la tuile : le pointeur sort de la tuile dès le
  //    premier centimètre, et un `pointerup` relâché ailleurs laisserait le geste ouvert à vie.
  useEffect(() => {
    if (!pointe) return;
    const move = (e: PointerEvent) => {
      const d = depart.current;
      if (!d) return;
      if (!d.saisi) {
        if (Math.hypot(e.clientX - d.x, e.clientY - d.y) < TILE_DRAG.threshold) return;
        const el = elements.current.get(d.key);
        if (!el) return;
        const r = el.getBoundingClientRect();
        prise.current = { dx: d.x - r.left, dy: d.y - r.top };
        d.saisi = true;
        setGrabbed(d.key);
        // ⚠️ `outerHTML` ET NON `innerHTML` : la tuile doit emporter SA PROPRE CLASSE, sinon les
        //    règles écrites sur elle (`.dt-col-row`) ne s'appliquent plus au clone.
        setGhostBox({ x: r.left, y: r.top, w: r.width, h: r.height, html: el.outerHTML });
        setApercu([...vif.current.keys]);
        return;
      }
      setGhostBox((g) => (g ? { ...g, x: e.clientX - prise.current.dx, y: e.clientY - prise.current.dy } : g));
      const v = vif.current;
      const tiles = v.mesurer().filter((t) => t.key !== d.key && estDeplacable(t.key, v.movable));
      const i = dropIndex(tiles, { x: e.clientX, y: e.clientY }, v.axis);
      const suivant = reorderTo(v.ordre, d.key, indexAfterRemoval(v.ordre, d.key, tiles[i]?.key));
      if (!memeOrdre(suivant, v.ordre)) { v.photographier(); setApercu(suivant); }
    };
    const up = () => vif.current.terminer(true);
    // `Échap` annule : tout reprend sa place, rien n'est écrit.
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); vif.current.terminer(false); } };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('keydown', key);
    };
    // ⚠️ `pointe` SEUL : tout le reste est lu dans `vif` au moment de l’événement, donc
    //    toujours à jour, sans reposer les écouteurs. C’est la raison d’être du miroir.
  }, [pointe]);

  const tile = useCallback((key: string) => ({
    ref: (el: HTMLElement | null) => { if (el) elements.current.set(key, el); else elements.current.delete(key); },
    // ⚠️ Styles de repos POSÉS DÈS LE DÉPART (charte §4) : appliqués à la saisie, le premier mouvement
    //    aurait déjà sélectionné le texte ou fait défiler la page.
    // En place, la tuile saisie s'estompe sans quitter sa case : c'est elle que les voisines contournent.
    style: { ...TILE_DRAG_REST, ...(inPlace && key === grabbed ? FADED : null) } as CSSProperties,
    'data-tile-key': key,
  }), [inPlace, grabbed]);

  const handle = useCallback((key: string) => ({
    onPointerDown: (e: ReactPointerEvent) => {
      // Bouton principal seulement : un clic droit ouvre un menu, il ne saisit pas.
      if (disabled || e.button !== 0 || !estDeplacable(key, movable)) return;
      // ⚠️ LE GESTE REFUSE DE PARTIR depuis une zone exclue (bouton posé sur la tuile) — voir
      // `ignoreSelector`. Le laisser partir puis compter sur le seuil laisserait l’action au hasard
      // du tremblement de la main.
      if (ignoreSelector && (e.target as Element | null)?.closest?.(ignoreSelector)) return;
      // Un atterrissage encore en vol s'achève d'un coup : un nouveau geste ne part pas sous un ancien fantôme.
      if (landing.current) { clearTimeout(landing.current); landing.current = null; setGhostBox(null); }
      depart.current = { key, x: e.clientX, y: e.clientY, saisi: false };
      setPointe(key);   // ⚠️ c'est LUI qui fait poser les écouteurs (voir sa déclaration)
      setGrabbed(null); // pas encore saisi : le seuil n'est pas franchi
      setApercu(null);
    },
    style: { cursor: disabled || !estDeplacable(key, movable) ? 'default' : 'grab', ...TILE_DRAG_REST } as CSSProperties,
  }), [disabled, movable, ignoreSelector]);

  // ⚠️ LES DIMENSIONS VIENNENT DE LA MESURE PRISE À LA SAISIE, pas d'une estimation : un creux plus
  //    petit que la tuile ferait sauter toute la liste au moment même où l'on vise (charte §4 :
  //    « MÊMES dimensions que la tuile »).
  const placeholder = grabbed && ghostBox && !inPlace
    ? (
      <div
        key={grabbed}
        aria-hidden
        ref={(el) => { if (el) elements.current.set(grabbed, el); else elements.current.delete(grabbed); }}
        data-tile-key={grabbed}
        style={{
          boxSizing: 'border-box', width: ghostBox.w, height: ghostBox.h, flexShrink: 0,
          borderWidth: TILE_DRAG.placeholder.borderWidth,
          borderStyle: TILE_DRAG.placeholder.borderStyle,
          borderColor: 'var(--teal-400)',
          borderRadius: TILE_DRAG.placeholder.radius,
          background: 'var(--teal-100)',
          ...TILE_DRAG_REST,
        }}
      />
    )
    : null;

  const ghost = ghostBox && typeof document !== 'undefined'
    ? createPortal(
      <div
        aria-hidden
        // ⚠️ LA CLASSE DE PORTÉE, empruntée au conteneur d'origine : c'est elle qui rend au clone les
        //    règles écrites en descendance. Sans elle il s'affichait VIDE (voir `ghostScope`).
        className={ghostScope}
        // ⚠️ `dangerouslySetInnerHTML` d'un HTML QUE NOUS VENONS DE LIRE dans notre propre DOM — ce
        //    n'est pas une entrée extérieure. Le rendre autrement obligerait l'appelant à décrire sa
        //    tuile une seconde fois, et les deux finiraient par différer.
        dangerouslySetInnerHTML={{ __html: ghostBox.html }}
        data-tile-ghost={ghostBox.landing ? 'landing' : ''}
        style={{
          position: 'fixed', left: ghostBox.x, top: ghostBox.y, width: ghostBox.w,
          zIndex: TILE_DRAG.ghost.zIndex, pointerEvents: 'none', opacity: TILE_DRAG.ghost.opacity,
          transform: `scale(${TILE_DRAG.ghost.scale})`, boxShadow: 'var(--sh-lifted)',
          borderRadius: TILE_DRAG.placeholder.radius, background: 'var(--paper)',
          // En place : le fantôme penche un peu (on voit ce qu'on tient) puis, au lâcher, rejoint sa case et s'efface.
          ...(inPlace ? {
            height: ghostBox.h, opacity: TILE_DRAG.inPlace.ghostOpacity,
            transform: `scale(${TILE_DRAG.ghost.scale}) rotate(${TILE_DRAG.inPlace.ghostRotate}deg)`,
          } : null),
          ...(ghostBox.landing ? {
            transition: `left ${TILE_DRAG.inPlace.landingMs}ms cubic-bezier(.2,.7,.3,1), top ${TILE_DRAG.inPlace.landingMs}ms cubic-bezier(.2,.7,.3,1), width ${TILE_DRAG.inPlace.landingMs}ms, height ${TILE_DRAG.inPlace.landingMs}ms, transform ${TILE_DRAG.inPlace.landingMs}ms, opacity 160ms 120ms, box-shadow ${TILE_DRAG.inPlace.landingMs}ms`,
            transform: 'none', opacity: 0, boxShadow: 'none',
          } : null),
          // ⚠️ ON EMPRUNTE LA PORTÉE, PAS LA BOÎTE : la classe du conteneur porte sa propre bordure,
          //    son rembourrage et sa hauteur bornée — appliqués ici, le fantôme deviendrait un second
          //    panneau flottant. Les styles en ligne les neutralisent, et gagnent sur la classe.
          padding: 0, border: 'none', margin: 0, maxHeight: 'none', minHeight: 0, overflow: 'visible',
          ...TILE_DRAG_REST,
        }}
      />,
      document.body,
    )
    : null;

  return { order: ordre, grabbed, tile, handle, placeholder, ghost };
}
